// Apolo IDE — SidePanel: Edits tab (Sprint v11 E6).
//
// Listens for `apolo:multiEdits` events {edits, root} carrying parsed XML
// edits from an LLM response. Renders the batch with per-edit accept toggles
// + bulk apply. Apply persists atomically via Electron file IPC and rolls
// back every change if any step fails.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, FileEdit, FilePlus, FileX, Loader2, X } from 'lucide-react';
import { applyEdits, validateEdits } from '../../../lib/multiFileEdits.js';
import { hasElectronBridge } from '../../../lib/electronBridge.js';
import { useAgentStore } from '../../../store/useAgentStore.js';

const ICONS = { edit: FileEdit, create: FilePlus, delete: FileX };
const COLORS = {
  edit: 'text-apolo-accent',
  create: 'text-apolo-success',
  delete: 'text-apolo-error',
};

function previewSnippet(text, max = 200) {
  if (typeof text !== 'string') return '';
  if (text.length <= max) return text;
  return text.slice(0, max) + '…';
}

export default function EditsTab() {
  const [state, setState] = useState({ edits: [], root: null, batchId: 0 });
  const [excluded, setExcluded] = useState(() => new Set());
  const [status, setStatus] = useState({ phase: 'idle', message: null });

  // Sprint v11 P4 — fallback to active session directory when event omits root.
  const activeSession = useAgentStore((s) => {
    const id = s.code.activeSessionId;
    return id ? s.code.sessions.find((sess) => sess.id === id) : null;
  });

  useEffect(() => {
    function onMultiEdits(e) {
      const payload = e.detail;
      if (!payload || !Array.isArray(payload.edits)) return;
      setState((prev) => ({
        edits: payload.edits,
        root: payload.root || activeSession?.directory || '',
        batchId: (prev?.batchId ?? 0) + 1,
      }));
      setExcluded(new Set());
      setStatus({ phase: 'idle', message: null });
    }
    window.addEventListener('apolo:multiEdits', onMultiEdits);
    return () => window.removeEventListener('apolo:multiEdits', onMultiEdits);
  }, [activeSession?.directory]);

  const activeEdits = useMemo(
    () => state.edits.filter((_, i) => !excluded.has(i)),
    [state.edits, excluded],
  );

  const toggleExclude = useCallback((idx) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const handleApply = useCallback(async () => {
    if (activeEdits.length === 0) return;
    const valid = validateEdits(activeEdits);
    if (!valid.ok) {
      setStatus({ phase: 'error', message: valid.error });
      return;
    }
    if (!hasElectronBridge() || !state.root) {
      setStatus({ phase: 'error', message: 'Electron file bridge ausente — abra no app.' });
      return;
    }
    setStatus({ phase: 'applying', message: null });
    const io = {
      read: async (p) => {
        try {
          const res = await window.apolo.file.read({ root: state.root, filePath: p });
          return typeof res?.content === 'string' ? res.content : null;
        } catch {
          return null;
        }
      },
      write: async (p, content) => {
        await window.apolo.file.write({ root: state.root, filePath: p, content });
      },
      remove: async (p) => {
        if (typeof window.apolo?.file?.remove === 'function') {
          await window.apolo.file.remove({ root: state.root, filePath: p });
        } else {
          // No remove IPC — fall back to empty write.
          await window.apolo.file.write({ root: state.root, filePath: p, content: '' });
        }
      },
    };
    const result = await applyEdits(activeEdits, io);
    if (result.ok) {
      setStatus({ phase: 'done', message: `${result.applied} edição(ões) aplicada(s).` });
      window.dispatchEvent(new CustomEvent('apolo:multiEditsApplied', {
        detail: { count: result.applied, edits: activeEdits },
      }));
    } else {
      setStatus({
        phase: 'error',
        message: `${result.error} — rollback de ${result.rolledBack} edição(ões).`,
      });
    }
  }, [activeEdits, state.root]);

  if (state.edits.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-4 py-6 text-center">
        <FileEdit size={20} className="text-apolo-textTertiary" aria-hidden="true" />
        <p className="max-w-xs text-sm text-apolo-textTertiary">
          Edições multi-arquivo (XML format) aparecem aqui para revisão atômica.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2">
        <div className="text-xs text-apolo-textSecondary">
          {state.edits.length} edição(ões) — {activeEdits.length} ativa(s)
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={handleApply}
            disabled={activeEdits.length === 0 || status.phase === 'applying'}
            className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-apolo-accentHover disabled:opacity-50"
          >
            {status.phase === 'applying' ? (
              <Loader2 size={11} className="animate-spin" aria-hidden="true" />
            ) : (
              <Check size={11} aria-hidden="true" />
            )}
            Aplicar
          </button>
        </div>
      </header>

      {status.message && (
        <div
          role="alert"
          className={`border-b px-3 py-1.5 text-xs ${
            status.phase === 'error'
              ? 'border-apolo-error/30 bg-apolo-errorBg text-apolo-error'
              : 'border-apolo-success/30 bg-apolo-successBg text-apolo-success'
          }`}
        >
          {status.phase === 'error' && <AlertCircle className="mr-1 inline" size={11} aria-hidden="true" />}
          {status.message}
        </div>
      )}

      <ul className="flex-1 divide-y divide-apolo-borderSubtle overflow-y-auto">
        {state.edits.map((edit, idx) => {
          const Icon = ICONS[edit.action] || FileEdit;
          const isExcluded = excluded.has(idx);
          return (
            <li
              key={`${idx}-${edit.path}`}
              className={`flex flex-col gap-1.5 p-3 ${isExcluded ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <Icon size={12} className={COLORS[edit.action]} aria-hidden="true" />
                  <code className="truncate text-xs text-apolo-textPrimary">{edit.path}</code>
                  <span className="rounded bg-apolo-surface3 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-apolo-textTertiary">
                    {edit.action}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => toggleExclude(idx)}
                  aria-label={isExcluded ? `Reincluir ${edit.path}` : `Excluir ${edit.path}`}
                  className="rounded p-1 text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
                >
                  {isExcluded ? <Check size={11} aria-hidden="true" /> : <X size={11} aria-hidden="true" />}
                </button>
              </div>
              {edit.oldContent && (
                <pre className="overflow-hidden rounded bg-apolo-error/10 p-2 text-[11px] text-apolo-error">
                  <code>- {previewSnippet(edit.oldContent)}</code>
                </pre>
              )}
              {edit.newContent && (
                <pre className="overflow-hidden rounded bg-apolo-success/10 p-2 text-[11px] text-apolo-success">
                  <code>+ {previewSnippet(edit.newContent)}</code>
                </pre>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
